import { useLazyQuery } from '@apollo/client'
import { Query } from '@danone-global/ct/interfaces'
import {
  GET_PRODUCT_CATEGORIES,
  GET_RELATED_PRODUCTS_QUERY,
} from '@danone-global/internal/graphql'
import { formatMoney } from '@danone-global/internal/react/components/price'
import {
  Grid,
  Hr,
  Typography,
} from '@danone-global/internal/react/components/ui'
import {
  calculateSubTotal,
  isValue,
  Theme,
  useCart,
  useCore,
} from '@danone-global/internal/react/core'
import {
  getChannelAvailability,
  useChannelId,
} from '@danone-global/internal/react/modules/stock'
import { makeStyles } from '@material-ui/styles'
import clsx from 'clsx'
import React, { useEffect } from 'react'
import { FormattedMessage } from 'react-intl'

import RelatedProduct from './related-product'
import { getCategoriesFromProducts } from './related-products.utils'

export interface Props {
  allowedCartSkus?: string[]

  variant?: 'default' | 'quick-view'

  withAnalytics?: boolean
}

export const useStyles = makeStyles(
  (theme: Theme) => ({
    root: {},

    default: {
      '& $products': {
        [theme.breakpoints.up('sm')]: {
          flexDirection: 'row',
        },

        [theme.breakpoints.between('sm', 'md')]: {
          '& > div:last-child': {
            display: 'none',
          },
        },
      },

      '& $title': {
        margin: theme.spacing(3, 0, 2.5),

        [theme.breakpoints.mobile()]: {
          margin: theme.spacing(0, 0, 2),
        },
      },
    },

    'quick-view': {
      '& $hr': {
        display: 'none',
      },
    },

    title: {
      margin: theme.spacing(3, 0, 2),
    },

    products: {
      marginTop: theme.spacing(),
    },

    hr: {
      borderTopColor: theme.palette.text.primary,
      borderTopStyle: 'dashed',
      borderTopWidth: 2,
      marginBottom: theme.spacing(5),

      [theme.breakpoints.mobile()]: {
        display: 'none',
      },
    },
  }),
  { name: 'RelatedProducts' },
)

export const RelatedProducts: React.FC<Props> = ({
  allowedCartSkus = [],
  withAnalytics = true,
  variant = 'default',
}) => {
  const [cart] = useCart()
  const classes = useStyles()
  const core = useCore()
  const channelId = useChannelId()
  const [getRelatedProducts, { data: relatedProductsData }] =
    useLazyQuery<Query>(GET_RELATED_PRODUCTS_QUERY)
  const [getProductCategories, { data: productCategoriesData }] =
    useLazyQuery<Query>(GET_PRODUCT_CATEGORIES)

  const lineItemSkus = React.useMemo(
    () => cart?.lineItems.map(({ variant: { sku } }) => sku),
    [cart?.lineItems],
  )

  const skus = React.useMemo(
    () => (allowedCartSkus.length > 0 ? allowedCartSkus : lineItemSkus),
    [lineItemSkus, allowedCartSkus],
  )

  const lineItemsStoreKeys = cart?.lineItems.map(
    (l) =>
      l?.variant?.attributesRaw?.find((i) => i.name === 'storeKey')?.value ||
      '',
  )

  // Only keep products with the same storekey as in the cart
  const filteredRelatedProductsData = relatedProductsData?.products?.results
    ?.filter((p) => {
      const storeKey =
        p?.masterData?.current?.masterVariant?.attributesRaw?.find(
          (i) => i.name === 'storeKey',
        )?.value || ''

      return lineItemsStoreKeys.includes(storeKey)
    })
    .filter((i) => i.masterData.current.masterVariant.price !== null)

  useEffect(() => {
    getProductCategories({
      variables: {
        skus: skus.filter(isValue),
        locale: core.localeConfig.locale,
      },
    })
  }, [skus, getProductCategories, core.localeConfig.locale])

  useEffect(() => {
    if (productCategoriesData?.products?.results?.length > 0) {
      const categories = getCategoriesFromProducts(
        productCategoriesData.products.results,
      )

      // Double check to make sure we have categories
      if (categories.length > 0) {
        getRelatedProducts({
          variables: {
            where: `masterData(current(categories(id in (${categories
              .map((category) => `"${category}"`)
              .join(',')}))))`,
            currency: core.localeConfig.currency,
            country: core.localeConfig.country,
            locale: core.localeConfig.locale,
          },
        })
      }
    }
  }, [
    productCategoriesData?.products,
    core.localeConfig.currency,
    core.localeConfig.country,
    getRelatedProducts,
  ])

  const amountNeededForFreeShipping = React.useMemo(() => {
    const amountToBeAdded =
      cart?.shippingInfo?.shippingRate?.freeAbove?.centAmount -
      calculateSubTotal(cart)?.centAmount

    if (isNaN(amountToBeAdded) || amountToBeAdded <= 0) {
      return false
    }

    return formatMoney(
      core.localeConfig.locale,
      cart?.totalPrice.currencyCode,
      amountToBeAdded,
      cart?.totalPrice?.fractionDigits,
    )
  }, [
    cart?.shippingInfo?.shippingRate?.freeAbove?.centAmount,
    cart?.totalPrice.centAmount,
    cart?.totalPrice.currencyCode,
    cart?.totalPrice?.fractionDigits,
    core.localeConfig.locale,
  ])

  const relatedProducts = React.useMemo(() => {
    if (
      !filteredRelatedProductsData ||
      filteredRelatedProductsData?.length === 0
    ) {
      return []
    }

    return (
      [...filteredRelatedProductsData]
        .filter((relatedProduct) => {
          const masterVariant = relatedProduct.masterData.current.masterVariant

          // Filter out all the products that we already have in the cart
          if (!lineItemSkus.includes(masterVariant.sku)) {
            const {
              availability: {
                channels: { results },
              },
            } = masterVariant

            const channel = getChannelAvailability(results, channelId)

            // Make sure the product is in stock
            return channel.isOnStock || channel.available
          }

          return false
        })
        // Remove all falsy values
        .filter(Boolean)
        // Shuffle the list
        .sort(() => Math.random() - 0.5)
        // We want to show max 2 items in quick view
        // .slice(0, 2)
        .slice(0, variant === 'quick-view' ? 2 : 3)
    )
  }, [filteredRelatedProductsData, lineItemSkus])

  if (relatedProducts.length === 0) {
    return null
  }

  return (
    <div className={clsx(classes.root, classes[variant])}>
      <Hr className={classes.hr} />

      <Typography
        className={classes.title}
        data-testid="related-products.title"
        variant={variant === 'default' ? 'h4' : 'body'}
      >
        {amountNeededForFreeShipping && (
          <FormattedMessage
            defaultMessage="Add {amount} worth of products, and receive free shipping"
            id="related-products.add-more-for-free-shipping"
            values={{
              amount: amountNeededForFreeShipping,
            }}
          />
        )}

        {!amountNeededForFreeShipping && (
          <FormattedMessage
            defaultMessage="You may also like"
            id="related-products.title"
          />
        )}
      </Typography>

      <Grid
        className={classes.products}
        data-testid="related-products"
        direction="column"
        spacing={16}
        container
      >
        {relatedProducts.map((relatedProduct) => (
          <RelatedProduct
            key={relatedProduct.id}
            product={relatedProduct}
            variant={variant}
            withAnalytics={withAnalytics}
          />
        ))}
      </Grid>
    </div>
  )
}

export default RelatedProducts
